import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { IForms106, IPaychecks, IPayslips } from 'API/Paychecks/paychecks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleX } from 'Images';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { monthsList, monthTextToNumber } from 'Utils';
import { TabPaychecks } from '../TabPaychecks';
import { TabTofes106 } from '../TabTofes106';
import { ControlledSelectInput2, NoResults2 } from 'Components';

interface ITabs {
  tabValue: number;
  data: IPaychecks;
}

enum TAB_NAME {
  paychecks = 0,
  tofes106 = 1,
}

export const PaychecksList = ({ tabValue, data }: ITabs) => {
  const [filterByMonth, setFilterByMonth] = useState('');
  const [filterByYear, setFilterByYear] = useState('');
  const [paychecksFiltered, setPaychecksFiltered] = useState<IPayslips[]>(
    data.payslips
  );
  const [form106Filtered, setForm106Filtered] = useState<IForms106[]>(
    data.forms106
  );
  const [activeTab, setActiveTab] = useState(TAB_NAME.paychecks);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Paychecks);
  const isDesktop = useIsDesktop();

  const years106List = useMemo(
    () =>
      data.forms106
        ? Array.from(new Set(data.forms106.map((item) => item.year)))
        : [],
    [data.forms106]
  );
  const yearsPaychecksList = useMemo(
    () =>
      data.payslips
        ? Array.from(new Set(data.payslips.map((item) => item.year)))
        : [],
    [data.payslips]
  );

  useEffect(() => {
    setPaychecksFiltered(data.payslips);
    setForm106Filtered(data.forms106);
  }, [data]);

  const resetFilters = useCallback(() => {
    setFilterByMonth('');
    setFilterByYear('');
    setPaychecksFiltered(data.payslips);
    setForm106Filtered(data.forms106);
  }, [data.forms106, data.payslips]);

  // filter for payslips tab
  useEffect(() => {
    let payslipsFilteredArray = data.payslips;
    if (tabValue === TAB_NAME.paychecks && filterByMonth !== '') {
      const month = monthTextToNumber(filterByMonth);
      if (month > -1) {
        payslipsFilteredArray = payslipsFilteredArray.filter(
          (item) => parseInt(item.month) === month
        );
      }
    }
    if (filterByYear !== '') {
      if (TAB_NAME.paychecks === tabValue) {
        payslipsFilteredArray = payslipsFilteredArray.filter(
          (item) => parseInt(item.year) === parseInt(filterByYear)
        );
      } else {
        // filter for forms106 tab
        let forms106FilteredArray = data.forms106;
        if (filterByYear !== '') {
          forms106FilteredArray = forms106FilteredArray.filter(
            (item) => parseInt(item.year) === parseInt(filterByYear)
          );
        }
        setForm106Filtered(forms106FilteredArray);
      }
    }
    setPaychecksFiltered(payslipsFilteredArray);
  }, [
    data.payslips,
    data.forms106,
    filterByMonth,
    filterByYear,
    years106List,
    tabValue,
  ]);

  // reset filters when switching tab
  useEffect(() => {
    if (activeTab !== tabValue) {
      setActiveTab(tabValue);
      resetFilters();
    }
  }, [activeTab, tabValue, resetFilters]);

  return (
    <>
      <Stack gap={isDesktop ? '32px' : '24px'}>
        <Stack
          direction={isDesktop ? 'row' : 'column'}
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          width='100%'
          role='tabpanel'
        >
          <Stack
            flexDirection={isDesktop ? 'row' : 'column'}
            justifyContent={'space-between'}
            gap={'32px'}
            marginRight={tabValue === TAB_NAME.paychecks ? '0' : '32px'}
            width={'100%'}
          >
            {tabValue === TAB_NAME.paychecks && (
              <Box width={isDesktop ? '50%' : '100%'}>
                <ControlledSelectInput2
                  size={'small'}
                  label={dictionary.month}
                  options={monthsList}
                  value={filterByMonth}
                  onChange={(e) => setFilterByMonth(e.target.value)}
                />
              </Box>
            )}
            <Box width={isDesktop ? '50%' : '100%'}>
              <ControlledSelectInput2
                size={'small'}
                label={dictionary.year}
                options={
                  tabValue === TAB_NAME.paychecks
                    ? yearsPaychecksList
                    : years106List
                }
                value={filterByYear}
                onChange={(e) => setFilterByYear(e.target.value)}
              />
            </Box>
          </Stack>
        </Stack>
        {(filterByYear || filterByMonth) && (
          <Button
            variant='textBtn'
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-end',
              textDecoration: 'none',
            }}
            onClick={resetFilters}
          >
            <Avatar
              src={PurpleX}
              alt={dictionary.resetFilter}
              sx={{ height: '24px', width: '24px' }}
            />
            <Typography variant='body2' color='primary'>
              {dictionary.resetFilter}
            </Typography>
          </Button>
        )}
      </Stack>
      <Box position={'relative'}>
        {activeTab === TAB_NAME.paychecks ? (
          paychecksFiltered?.length > 0 ? (
            <Stack gap={isDesktop ? '32px' : '20px'}>
              {!filterByMonth && !filterByYear && (
                <Typography variant='body1' component='h2'>
                  {dictionary.recentPaychecks}
                </Typography>
              )}
              <TabPaychecks data={paychecksFiltered} />
            </Stack>
          ) : (
            <Box height={isDesktop ? '400px' : '180px'}>
              <NoResults2 textSearch={dictionary.noResultSuggestion} />
            </Box>
          )
        ) : paychecksFiltered?.length > 0 ? (
          <Stack gap={isDesktop ? '32px' : '20px'}>
            {!filterByYear && (
              <Typography variant='body1' component='h2'>
                {dictionary.recentTofes106}
              </Typography>
            )}
            <TabTofes106 data={form106Filtered} />
          </Stack>
        ) : (
          <Box height={isDesktop ? '400px' : '180px'}>
            <NoResults2 textSearch={dictionary.noResultSuggestion} />
          </Box>
        )}
      </Box>
    </>
  );
};
